import React, { useState, useEffect } from "react";
import { useAuth } from "../utils/AuthContext";
import api from "../utils/api";

const ProfileDashboard = () => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [profileData, setProfileData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Form states
  const [emailPreferences, setEmailPreferences] = useState({
    newReleases: false,
    upcomingReleases: false,
    wishlistUpdates: false,
    weeklyDigest: false
  });
  const [discordSettings, setDiscordSettings] = useState({
    connected: false,
    linked: false,
    guildId: null,
    channelId: null,
    discordUserId: null,
    notifications: {
      newReleases: false,
      upcomingReleases: false,
      wishlistUpdates: false,
      weeklyDigest: false
    }
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  useEffect(() => {
    if (!authLoading && isAuthenticated && user) {
      fetchProfileData();
    } else if (!authLoading && !isAuthenticated) {
      setLoading(false);
      setMessage({ type: "error", text: "Please log in to access your profile dashboard" });
    }
  }, [authLoading, isAuthenticated, user]);

  // Handle OAuth2 callback parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const discordConnected = urlParams.get('discord_connected');
    const discordError = urlParams.get('discord_error');
    const discordUserId = urlParams.get('discord_user_id');
    
    if (discordConnected === 'true') {
      setMessage({ type: "success", text: `Discord account linked successfully! Discord User ID: ${discordUserId}` });
      // Refresh Discord status
      fetchProfileData();
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (discordError === 'true') {
      setMessage({ type: "error", text: "Failed to link Discord account. Please try again." });
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const [profileResponse, statsResponse] = await Promise.all([
        api.get('/profile'),
        api.get('/stats')
      ]);
      
      setProfileData(profileResponse.data);
      setStats(statsResponse.data);
      
      // Set form data from profile
      if (profileResponse.data.email_preferences) {
        setEmailPreferences(JSON.parse(profileResponse.data.email_preferences));
      }
             // Fetch Discord bot status
       const discordStatusResponse = await api.get('/discord/status');
       const discordSettingsResponse = await api.get('/discord/settings');
       
       setDiscordSettings({
         connected: discordStatusResponse.data.connected,
         linked: discordStatusResponse.data.linked,
         guildId: discordStatusResponse.data.guildId,
         channelId: discordStatusResponse.data.channelId,
         discordUserId: discordStatusResponse.data.discordUserId,
         notifications: discordSettingsResponse.data
       });
    } catch (error) {
      console.error('Error fetching profile data:', error);
      setMessage({ type: "error", text: "Failed to load profile data" });
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async () => {
    try {
      setSaving(true);
      await api.put('/profile', {
        email_preferences: JSON.stringify(emailPreferences)
      });
      setMessage({ type: "success", text: "Profile updated successfully!" });
    } catch (error) {
      setMessage({ type: "error", text: "Failed to update profile" });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setMessage({ type: "error", text: "New passwords don't match" });
      return;
    }

    try {
      setSaving(true);
      await api.put('/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      setMessage({ type: "success", text: "Password changed successfully!" });
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      setMessage({ type: "error", text: error.response?.data?.error || "Failed to change password" });
    } finally {
      setSaving(false);
    }
  };

  const handleDiscordConnect = async () => {
    try {
      setSaving(true);
      // Get the OAuth2 authorization URL from the backend
      const response = await api.get('/discord/status');
      if (response.data.botInviteUrl) {
        // Open the OAuth2 authorization URL in the same window
        window.location.href = response.data.botInviteUrl;
      } else {
        setMessage({ type: "error", text: "Failed to get Discord authorization URL" });
      }
    } catch (error) {
      console.error('Discord connect error:', error);
      setMessage({ type: "error", text: "Failed to get Discord authorization URL" });
    } finally {
      setSaving(false);
    }
  };

  const handleDiscordDisconnect = async () => {
    try {
      setSaving(true);
      await api.post('/discord/disconnect');
      setDiscordSettings(prev => ({ ...prev, connected: false, guildId: null, channelId: null }));
      setMessage({ type: "success", text: "Discord bot disconnected successfully!" });
    } catch (error) {
      setMessage({ type: "error", text: "Failed to disconnect Discord bot" });
    } finally {
      setSaving(false);
    }
  };

  const handleTestDiscord = async () => {
    if (!discordSettings.connected) {
      setMessage({ type: "error", text: "Discord bot is not connected" });
      return;
    }

    try {
      setSaving(true);
      await api.post('/discord/test');
      setMessage({ type: "success", text: "Test message sent to Discord channel!" });
    } catch (error) {
      setMessage({ type: "error", text: "Failed to send test message" });
    } finally {
      setSaving(false);
    }
  };

  const handleDiscordSettingsUpdate = async () => {
    try {
      setSaving(true);
      await api.put('/discord/settings', {
        notifications: discordSettings.notifications
      });
      setMessage({ type: "success", text: "Discord notification settings updated!" });
    } catch (error) {
      setMessage({ type: "error", text: "Failed to update Discord settings" });
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setMessage({ type: "", text: "" }); // Clear any existing messages when switching tabs
  };

  const tabs = [
    { id: "overview", name: "Overview", icon: "📊" },
    { id: "email", name: "Email Preferences", icon: "📧" },
    { id: "security", name: "Security", icon: "🔒" },
    { id: "discord", name: "Discord Integration", icon: "🎮" }
  ];

  if (authLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-white">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-700 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-700 rounded w-1/2 mb-8"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="h-32 bg-gray-700 rounded"></div>
            <div className="h-32 bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-white">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Profile Dashboard</h1>
          <p className="text-gray-300 mb-8">Please log in to access your profile dashboard</p>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('showLoginModal'))}
            className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Log In
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-white">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-700 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-700 rounded w-1/2 mb-8"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="h-32 bg-gray-700 rounded"></div>
            <div className="h-32 bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 text-white">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Profile Dashboard</h1>
        <p className="text-gray-300">Welcome back, {profileData?.username || user?.username || 'PlayerOne'}!</p>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.type === "success" ? "bg-green-600" : "bg-red-600"
        }`}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-8 border-b border-gray-700">
                 {tabs.map((tab) => (
           <button
             key={tab.id}
             onClick={() => handleTabChange(tab.id)}
             className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
               activeTab === tab.id
                 ? "bg-blue-600 text-white"
                 : "bg-gray-700 text-gray-300 hover:bg-gray-600"
             }`}
           >
             <span className="mr-2">{tab.icon}</span>
             {tab.name}
           </button>
         ))}
      </div>

      {/* Tab Content */}
      <div className="bg-gray-800 rounded-lg p-6">
        {activeTab === "overview" && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gray-700 p-4 rounded-lg">
                <div className="text-2xl font-bold text-blue-400">{stats?.trackedGames || 0}</div>
                <div className="text-gray-300">Tracked Games</div>
              </div>
              <div className="bg-gray-700 p-4 rounded-lg">
                <div className="text-2xl font-bold text-green-400">{stats?.wishlistedGames || 0}</div>
                <div className="text-gray-300">Wishlisted Games</div>
              </div>
              <div className="bg-gray-700 p-4 rounded-lg">
                <div className="text-2xl font-bold text-yellow-400">{stats?.ratedGames || 0}</div>
                <div className="text-gray-300">Rated Games</div>
              </div>
              <div className="bg-gray-700 p-4 rounded-lg">
                <div className="text-2xl font-bold text-purple-400">{stats?.likedGames || 0}</div>
                <div className="text-gray-300">Liked Games</div>
              </div>
            </div>
            
            <div className="bg-gray-700 p-4 rounded-lg">
              <h3 className="text-lg font-semibold mb-3">Account Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                 <div>
                   <label className="block text-gray-300 text-sm mb-1">Username</label>
                   <div className="text-white">{profileData?.username || 'N/A'}</div>
                 </div>
                 <div>
                   <label className="block text-gray-300 text-sm mb-1">Email</label>
                   <div className="text-white">{profileData?.email || user?.email || 'N/A'}</div>
                 </div>
                 <div>
                   <label className="block text-gray-300 text-sm mb-1">Member Since</label>
                   <div className="text-white">
                     {profileData?.created_at ? new Date(profileData.created_at).toLocaleDateString() : 'N/A'}
                   </div>
                 </div>
              </div>
            </div>
          </div>
        )}

                 {activeTab === "email" && (
           <div className="space-y-6">
             <h3 className="text-xl font-semibold mb-4">Email Notification Preferences</h3>
             
             <div className="bg-yellow-600/20 border border-yellow-600/30 rounded-lg p-4 mb-6">
               <div className="flex items-center">
                 <span className="text-yellow-400 mr-2">⚠️</span>
                 <span className="text-yellow-200 font-medium">Email notifications coming soon!</span>
               </div>
               <p className="text-yellow-100 text-sm mt-1">These settings will be used when email notifications are implemented.</p>
             </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div>
                  <div className="font-medium">New Game Releases</div>
                  <div className="text-sm text-gray-300">Get notified when new games are released</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={emailPreferences.newReleases}
                    onChange={(e) => setEmailPreferences(prev => ({
                      ...prev,
                      newReleases: e.target.checked
                    }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div>
                  <div className="font-medium">Upcoming Releases</div>
                  <div className="text-sm text-gray-300">Get notified about upcoming game releases</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={emailPreferences.upcomingReleases}
                    onChange={(e) => setEmailPreferences(prev => ({
                      ...prev,
                      upcomingReleases: e.target.checked
                    }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div>
                  <div className="font-medium">Wishlist Updates</div>
                  <div className="text-sm text-gray-300">Get notified when games on your wishlist are updated</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={emailPreferences.wishlistUpdates}
                    onChange={(e) => setEmailPreferences(prev => ({
                      ...prev,
                      wishlistUpdates: e.target.checked
                    }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                <div>
                  <div className="font-medium">Weekly Digest</div>
                  <div className="text-sm text-gray-300">Receive a weekly summary of your gaming activity</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={emailPreferences.weeklyDigest}
                    onChange={(e) => setEmailPreferences(prev => ({
                      ...prev,
                      weeklyDigest: e.target.checked
                    }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>

            <button
              onClick={handleProfileUpdate}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 px-6 py-2 rounded-lg font-medium transition-colors"
            >
              {saving ? "Saving..." : "Save Email Preferences"}
            </button>
          </div>
        )}

        {activeTab === "security" && (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold mb-4">Security Settings</h3>
            
            <div className="bg-gray-700 p-6 rounded-lg">
              <h4 className="text-lg font-medium mb-4">Change Password</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-300 text-sm mb-1">Current Password</label>
                  <input
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm(prev => ({
                      ...prev,
                      currentPassword: e.target.value
                    }))}
                    className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    placeholder="Enter current password"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-1">New Password</label>
                  <input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm(prev => ({
                      ...prev,
                      newPassword: e.target.value
                    }))}
                    className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    placeholder="Enter new password"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-1">Confirm New Password</label>
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm(prev => ({
                      ...prev,
                      confirmPassword: e.target.value
                    }))}
                    className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    placeholder="Confirm new password"
                  />
                </div>
                <button
                  onClick={handlePasswordChange}
                  disabled={saving || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-gray-600 px-6 py-2 rounded-lg font-medium transition-colors"
                >
                  {saving ? "Changing..." : "Change Password"}
                </button>
              </div>
            </div>
          </div>
        )}

                          {activeTab === "discord" && (
           <div className="space-y-6">
             <h3 className="text-xl font-semibold mb-4">Discord Bot Integration</h3>
             
             <div className="bg-blue-600/20 border border-blue-600/30 rounded-lg p-4 mb-6">
               <div className="flex items-center">
                 <span className="text-blue-400 mr-2">🤖</span>
                 <span className="text-blue-200 font-medium">Discord Bot Integration</span>
               </div>
               <p className="text-blue-100 text-sm mt-1">Connect our Discord bot to your server for game notifications and updates.</p>
             </div>
             
             {/* Connection Status */}
             <div className="bg-gray-700 p-6 rounded-lg">
               <h4 className="text-lg font-medium mb-4">Bot Connection</h4>
               
               <div className="space-y-4">
                 <div className="flex items-center justify-between">
                   <div>
                     <div className="font-medium">Account Linking</div>
                     <div className="text-sm text-gray-300">
                       {discordSettings.linked ? 'OP Game Tracker account linked to Discord' : 'Account not linked to Discord'}
                     </div>
                   </div>
                   <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                     discordSettings.linked 
                       ? 'bg-green-600 text-white' 
                       : 'bg-gray-600 text-gray-300'
                   }`}>
                     {discordSettings.linked ? 'Linked' : 'Not Linked'}
                   </div>
                 </div>

                 <div className="flex items-center justify-between">
                   <div>
                     <div className="font-medium">Bot Connection</div>
                     <div className="text-sm text-gray-300">
                       {discordSettings.connected ? 'Bot connected to Discord server' : 'Bot not connected to server'}
                     </div>
                   </div>
                   <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                     discordSettings.connected 
                       ? 'bg-green-600 text-white' 
                       : 'bg-gray-600 text-gray-300'
                   }`}>
                     {discordSettings.connected ? 'Connected' : 'Disconnected'}
                   </div>
                 </div>
                 
                 {discordSettings.connected && (
                   <div className="text-sm text-gray-300">
                     <div>Server ID: {discordSettings.guildId}</div>
                     <div>Channel ID: {discordSettings.channelId}</div>
                   </div>
                 )}

                 {discordSettings.linked && (
                   <div className="text-sm text-gray-300">
                     <div>Discord User ID: {discordSettings.discordUserId}</div>
                   </div>
                 )}
                 
                 <div className="flex gap-3">
                   {!discordSettings.linked ? (
                     <button
                       onClick={handleDiscordConnect}
                       disabled={saving}
                       className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 px-4 py-2 rounded-lg font-medium transition-colors"
                     >
                       {saving ? "Loading..." : "Link Discord Account"}
                     </button>
                   ) : !discordSettings.connected ? (
                     <div className="space-y-2">
                       <p className="text-sm text-gray-300">Account linked! Now connect the bot to a server:</p>
                       <button
                         onClick={() => window.open('https://discord.com/api/oauth2/authorize?client_id=' + process.env.REACT_APP_DISCORD_CLIENT_ID + '&permissions=2048&scope=bot', '_blank')}
                         className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg font-medium transition-colors"
                       >
                         Add Bot to Server
                       </button>
                     </div>
                   ) : (
                     <button
                       onClick={handleDiscordDisconnect}
                       disabled={saving}
                       className="bg-red-600 hover:bg-red-700 disabled:bg-gray-600 px-4 py-2 rounded-lg font-medium transition-colors"
                     >
                       {saving ? "Disconnecting..." : "Disconnect Bot"}
                     </button>
                   )}
                   
                   {discordSettings.connected && (
                     <button
                       onClick={handleTestDiscord}
                       disabled={saving}
                       className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 px-4 py-2 rounded-lg font-medium transition-colors"
                     >
                       {saving ? "Sending..." : "Test Message"}
                     </button>
                   )}
                 </div>
               </div>
             </div>
             
             {/* Notification Settings */}
             <div className="bg-gray-700 p-6 rounded-lg">
               <h4 className="text-lg font-medium mb-4">Notification Settings</h4>
               <p className="text-sm text-gray-300 mb-4">Choose which notifications to receive in your Discord channel:</p>
               
               <div className="space-y-4">
                 <div className="flex items-center justify-between p-4 bg-gray-600 rounded-lg">
                   <div>
                     <div className="font-medium">New Game Releases</div>
                     <div className="text-sm text-gray-300">Get notified when new games are released</div>
                   </div>
                   <label className="relative inline-flex items-center cursor-pointer">
                     <input
                       type="checkbox"
                       checked={discordSettings.notifications.newReleases}
                       onChange={(e) => setDiscordSettings(prev => ({
                         ...prev,
                         notifications: {
                           ...prev.notifications,
                           newReleases: e.target.checked
                         }
                       }))}
                       className="sr-only peer"
                     />
                     <div className="w-11 h-6 bg-gray-500 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                   </label>
                 </div>

                 <div className="flex items-center justify-between p-4 bg-gray-600 rounded-lg">
                   <div>
                     <div className="font-medium">Upcoming Releases</div>
                     <div className="text-sm text-gray-300">Get notified about upcoming game releases</div>
                   </div>
                   <label className="relative inline-flex items-center cursor-pointer">
                     <input
                       type="checkbox"
                       checked={discordSettings.notifications.upcomingReleases}
                       onChange={(e) => setDiscordSettings(prev => ({
                         ...prev,
                         notifications: {
                           ...prev.notifications,
                           upcomingReleases: e.target.checked
                         }
                       }))}
                       className="sr-only peer"
                     />
                     <div className="w-11 h-6 bg-gray-500 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                   </label>
                 </div>

                 <div className="flex items-center justify-between p-4 bg-gray-600 rounded-lg">
                   <div>
                     <div className="font-medium">Wishlist Updates</div>
                     <div className="text-sm text-gray-300">Get notified when games on your wishlist are updated</div>
                   </div>
                   <label className="relative inline-flex items-center cursor-pointer">
                     <input
                       type="checkbox"
                       checked={discordSettings.notifications.wishlistUpdates}
                       onChange={(e) => setDiscordSettings(prev => ({
                         ...prev,
                         notifications: {
                           ...prev.notifications,
                           wishlistUpdates: e.target.checked
                         }
                       }))}
                       className="sr-only peer"
                     />
                     <div className="w-11 h-6 bg-gray-500 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                   </label>
                 </div>

                 <div className="flex items-center justify-between p-4 bg-gray-600 rounded-lg">
                   <div>
                     <div className="font-medium">Weekly Digest</div>
                     <div className="text-sm text-gray-300">Receive a weekly summary of your gaming activity</div>
                   </div>
                   <label className="relative inline-flex items-center cursor-pointer">
                     <input
                       type="checkbox"
                       checked={discordSettings.notifications.weeklyDigest}
                       onChange={(e) => setDiscordSettings(prev => ({
                         ...prev,
                         notifications: {
                           ...prev.notifications,
                           weeklyDigest: e.target.checked
                         }
                       }))}
                       className="sr-only peer"
                     />
                     <div className="w-11 h-6 bg-gray-500 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                   </label>
                 </div>
               </div>
               
               <button
                 onClick={handleDiscordSettingsUpdate}
                 disabled={saving}
                 className="mt-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 px-6 py-2 rounded-lg font-medium transition-colors"
               >
                 {saving ? "Saving..." : "Save Notification Settings"}
               </button>
             </div>
             
             {/* Legal Links */}
             <div className="bg-gray-700 p-6 rounded-lg">
               <h4 className="text-lg font-medium mb-4">Legal Information</h4>
               <p className="text-sm text-gray-300 mb-4">By using our Discord bot, you agree to our terms and privacy policy:</p>
               
               <div className="space-y-3">
                 <a 
                   href="/discord-tos.html" 
                   target="_blank" 
                   rel="noopener noreferrer"
                   className="flex items-center text-blue-400 hover:text-blue-300 transition-colors"
                 >
                   <span className="mr-2">📄</span>
                   Discord ToS
                 </a>
                 <a 
                   href="/discord-privacy-policy.html" 
                   target="_blank" 
                   rel="noopener noreferrer"
                   className="flex items-center text-blue-400 hover:text-blue-300 transition-colors"
                 >
                   <span className="mr-2">🔒</span>
                   Discord Privacy Policy
                 </a>
               </div>
               
               <div className="mt-4 p-3 bg-yellow-600/20 border border-yellow-600/30 rounded-lg">
                 <div className="flex items-center">
                   <span className="text-yellow-400 mr-2">⚠️</span>
                   <span className="text-yellow-200 text-sm font-medium">Important</span>
                 </div>
                 <p className="text-yellow-100 text-sm mt-1">
                   These documents are required by Discord for bot applications. Please review them before using our Discord bot.
                 </p>
               </div>
             </div>
           </div>
         )}
      </div>
    </div>
  );
};

export default ProfileDashboard;
